import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchPendingApprovalCases, fetchPendingApprovalTimeEntries, fetchPendingApprovalExpenseEntries,
  approveCase, rejectCase, approveTimeEntry, rejectTimeEntry, approveExpenseEntry, rejectExpenseEntry,
} from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';

// "Pending My Approval" inbox: paralegal-created Cases/TimeEntries/
// ExpenseEntries awaiting a Partner/Associate's approve/reject decision.
// The backend already scopes the list endpoints to what this user (Partner
// firm-wide, Associate their own case team) is allowed to see, so this
// component just renders whatever comes back.
function PendingApprovalsInbox({ authAxios, showFormMessage, setActiveTab, setNavigateTo }) {
  const confirm = useConfirm();
  const [cases, setCases] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [expenseEntries, setExpenseEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const isBusyRef = useRef(false);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [c, t, e] = await Promise.all([
        fetchPendingApprovalCases(authAxios),
        fetchPendingApprovalTimeEntries(authAxios),
        fetchPendingApprovalExpenseEntries(authAxios),
      ]);
      setCases(c);
      setTimeEntries(t);
      setExpenseEntries(e);
    } catch (err) {
      showFormMessage?.('danger', 'Failed to load pending approvals.');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const viewInModule = (tab, type, id) => {
    if (setActiveTab) setActiveTab(tab);
    if (setNavigateTo) setNavigateTo({ tab, type, id, autoOpen: true });
  };

  const runAction = async (id, actionFn, successMessage) => {
    if (isBusyRef.current) return;
    isBusyRef.current = true;
    setBusyId(id);
    try {
      await actionFn();
      showFormMessage?.('success', successMessage);
      fetchAll();
    } catch (err) {
      showFormMessage?.('danger', err.response?.data?.detail || 'Action failed.');
    } finally {
      isBusyRef.current = false;
      setBusyId(null);
    }
  };

  const handleApprove = (id, label, approveFn) => async () => {
    const ok = await confirm({
      title: 'Approve', message: `Approve "${label}"? It will take effect immediately.`,
      confirmText: 'Approve', variant: 'primary',
    });
    if (!ok) return;
    runAction(id, () => approveFn(authAxios, id), 'Approved.');
  };

  const handleReject = (id, label, rejectFn) => async () => {
    const reason = await confirm({
      title: 'Reject', message: `Reject "${label}"? The creator can edit and resubmit it afterwards.`,
      promptMode: true, promptLabel: 'Reason for rejection', required: true,
      confirmText: 'Reject', variant: 'danger',
    });
    if (!reason) return;
    runAction(id, () => rejectFn(authAxios, id, reason), 'Rejected.');
  };

  const totalCount = cases.length + timeEntries.length + expenseEntries.length;

  if (isLoading && totalCount === 0) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Pending My Approval</h2>
        <p className="text-gray-500 text-sm mt-1">
          Cases and billing entries logged by paralegals, awaiting your sign-off before they take effect.
        </p>
      </div>

      {totalCount === 0 && !isLoading && (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <p className="text-gray-500">Nothing is waiting on your approval right now.</p>
        </div>
      )}

      {cases.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Pending Cases ({cases.length})</h3>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opened</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cases.map(c => (
                  <tr key={c.id} className="hover:bg-amber-50">
                    <td className="px-4 py-3 text-gray-800">{c.title}</td>
                    <td className="px-4 py-3 text-gray-600">{c.client_full_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.case_type_display || c.case_type}</td>
                    <td className="px-4 py-3 text-gray-600">{c.created_by_username || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.date_opened}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => viewInModule('cases', 'case', c.id)} aria-label={`View case: ${c.title}`} className="text-gray-600 hover:text-gray-900 text-xs font-medium">View</button>
                      <button disabled={busyId === c.id} onClick={handleApprove(c.id, c.title, approveCase)} aria-label={`Approve case: ${c.title}`} className="text-green-600 hover:text-green-800 text-xs font-medium disabled:opacity-50">Approve</button>
                      <button disabled={busyId === c.id} onClick={handleReject(c.id, c.title, rejectCase)} aria-label={`Reject case: ${c.title}`} className="text-red-600 hover:text-red-800 text-xs font-medium disabled:opacity-50">Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {timeEntries.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Pending Time Entries ({timeEntries.length})</h3>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matter</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Logged By</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {timeEntries.map(t => (
                  <tr key={t.id} className="hover:bg-amber-50">
                    <td className="px-4 py-3 text-gray-800">{t.case_title || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{t.description}</td>
                    <td className="px-4 py-3 text-gray-600">{t.created_by_username || '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{t.duration_hours}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button disabled={busyId === t.id} onClick={handleApprove(t.id, t.description, approveTimeEntry)} aria-label={`Approve time entry: ${t.description}`} className="text-green-600 hover:text-green-800 text-xs font-medium disabled:opacity-50">Approve</button>
                      <button disabled={busyId === t.id} onClick={handleReject(t.id, t.description, rejectTimeEntry)} aria-label={`Reject time entry: ${t.description}`} className="text-red-600 hover:text-red-800 text-xs font-medium disabled:opacity-50">Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {expenseEntries.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Pending Disbursements ({expenseEntries.length})</h3>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matter</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Logged By</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {expenseEntries.map(e => (
                  <tr key={e.id} className="hover:bg-amber-50">
                    <td className="px-4 py-3 text-gray-800">{e.case_title || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{e.description}</td>
                    <td className="px-4 py-3 text-gray-600">{e.created_by_username || '—'}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(e.amount || 0)}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button disabled={busyId === e.id} onClick={handleApprove(e.id, e.description, approveExpenseEntry)} aria-label={`Approve expense: ${e.description}`} className="text-green-600 hover:text-green-800 text-xs font-medium disabled:opacity-50">Approve</button>
                      <button disabled={busyId === e.id} onClick={handleReject(e.id, e.description, rejectExpenseEntry)} aria-label={`Reject expense: ${e.description}`} className="text-red-600 hover:text-red-800 text-xs font-medium disabled:opacity-50">Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default PendingApprovalsInbox;
